import React, { useState, useEffect } from 'react';
import Header from '../Header';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { letterheadAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { notify } from '../../utils/notify';
import { generateLetterheadPDF } from '../../utils/letterheadPDF';
import Topbar from "../../components/Topbar";

function LetterheadEditor() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', designation: '', department: '',
    address: 'TSE Workspace Office, Green City, Sector-10, Lucknow - 226010',
    phone: '+91 98765 43210', email: '', website: 'www.tseworkspace.com',
    refNo: '', date: new Date().toISOString().slice(0,10),
    templateId: searchParams.get('template') || '',
    template: searchParams.get('name') || 'Modern', remarks: '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (user) {
      set('name', user.userName || '');
      set('designation', user.designation || '');
      set('department', user.department || '');
      set('email', user.email || '');
    }
    letterheadAPI.listTemplates().then(d => setTemplates(d.templates || [])).catch(() => {});
  }, [user]);

  const handleSubmit = async (asDraft = false) => {
    if (!asDraft && (!form.name || !form.designation || !form.department)) {
      notify.error('Name, Designation and Department are required'); return;
    }
    const fn = asDraft ? setSaving : setSubmitting;
    fn(true);
    try {
      const templateId = form.templateId || templates[0]?._id;
      await letterheadAPI.createRequest({ ...form, templateId, saveAsDraft: asDraft });
      notify.success(asDraft ? 'Saved as draft successfully' : 'Letterhead submitted for approval!');
      if (!asDraft) setTimeout(() => navigate('/letterhead/approvals'), 1500);
    } catch (e) {
      notify.error(e.message || 'Failed to submit');
    } finally { fn(false); }
  };

  const handleDownloadPDF = () => {
    if (!form.name) { notify.warn('Fill in the employee name before downloading'); return; }
    generateLetterheadPDF({
      ...form,
      date: form.date ? new Date(form.date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN'),
    });
    notify.success('PDF downloaded successfully');
  };

  return (
    <>
      <Header />
      <div id="db-wrapper"><main id="page-content">
          <Topbar section="Letterhead" /><section className="container-fluid p-4 pt-3">
        <div className="page-hero d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h5 className="text-white lh-1">Letterhead Editor</h5>
            <div className="sub">Fill official details and preview your A4 letterhead in real-time</div>
          </div>
          <Link to="/letterhead/templates" className="btn fw-semibold" style={{background:'rgba(255,255,255,0.2)',color:'#fff',borderRadius:8,border:'1px solid rgba(255,255,255,0.3)',padding:'6px 14px'}}>
            <i className="bi bi-arrow-left me-2"/>Templates
          </Link>
        </div>

        <div className="row g-2 mt-1">
          {/* Form */}
          <div className="col-12 col-lg-5">
            <div className="kpi-card">
              <div style={{fontWeight:700,fontSize:'0.81rem',color:'#111827',marginBottom:8}}>
                <i className="bi bi-person-vcard me-2 text-primary"/>Employee Details
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Template</label>
                <select className="form-select" value={form.templateId}
                  onChange={e => { set('templateId', e.target.value); set('template', templates.find(t => t._id === e.target.value)?.name || 'Modern'); }}>
                  {templates.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
              </div>

              {[
                ['name',        'Full Name *'],
                ['designation', 'Designation *'],
                ['department',  'Department *'],
                ['refNo',       'Reference No.'],
                ['date',        'Date', 'date'],
                ['phone',       'Phone'],
                ['email',       'Email', 'email'],
                ['website',     'Website'],
              ].map(([k, lbl, type = 'text']) => (
                <div key={k} className="mb-3">
                  <label className="form-label fw-semibold">{lbl}</label>
                  <input type={type} className="form-control" value={form[k]} onChange={e => set(k, e.target.value)} />
                </div>
              ))}

              <div className="mb-3">
                <label className="form-label fw-semibold">Address</label>
                <textarea className="form-control" rows={2} value={form.address} onChange={e => set('address', e.target.value)} />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Remarks</label>
                <textarea className="form-control" rows={2} value={form.remarks} onChange={e => set('remarks', e.target.value)} placeholder="Optional remarks..." />
              </div>

              <div className="d-flex gap-2">
                <button className="btn btn-light flex-fill rounded-3" onClick={() => handleSubmit(true)} disabled={saving}>
                  {saving ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-floppy me-1"/>Save Draft</>}
                </button>
                <button className="btn flex-fill rounded-3 fw-semibold" style={{background:'linear-gradient(135deg,#1a5276,#1e8449)',color:'#fff',border:'none'}}
                  onClick={() => handleSubmit(false)} disabled={submitting}>
                  {submitting ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-send me-1"/>Submit for Approval</>}
                </button>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="col-12 col-lg-7">
            <div className="kpi-card p-0 overflow-hidden" style={{border:'1px solid #e5e7eb'}}>
              {/* Header bar */}
              <div style={{background:'linear-gradient(135deg,#1a5276 55%,#1e8449 100%)',padding:'13px 17px',color:'#fff',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:40,height:40,background:'rgba(255,255,255,0.2)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',border:'1.5px solid rgba(255,255,255,0.4)'}}>
                    <i className="bi bi-tree-fill" style={{color:'#fff',fontSize:'0.946rem'}}/>
                  </div>
                  <div>
                    <div style={{fontWeight:700,fontSize:'0.874rem'}}>TSE ENVIRONMENT</div>
                    <div style={{fontSize:'0.626rem',opacity:0.85}}>Official Letterhead — {form.template} Template</div>
                  </div>
                </div>
                <div style={{textAlign:'right',fontSize:'0.7rem',lineHeight:1.9}}>
                  <div><b>Ref:</b> {form.refNo || 'TSE/LET/2025-26/XXX'}</div>
                  <div><b>Date:</b> {form.date ? new Date(form.date).toLocaleDateString('en-IN') : ''}</div>
                </div>
              </div>

              {/* Body */}
              <div style={{padding:17,position:'relative',minHeight:340,background:'#fff'}}>
                {/* QR code block */}
                <div style={{position:'absolute',top:16,right:16,width:45,height:45,border:'1.5px solid #1a5276',borderRadius:6,background:'#f8fafc',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:2}}>
                  <i className="bi bi-qr-code" style={{fontSize:32,color:'#1a5276'}}/>
                  <span style={{fontSize:7,color:'#1a5276',marginTop:2,textAlign:'center',lineHeight:1.2}}>Scan to<br/>verify</span>
                </div>

                <div style={{fontSize:20,fontWeight:700,color:'#1a5276',marginBottom:3}}>{form.name || <span style={{color:'#d1d5db'}}>Employee Name</span>}</div>
                <div style={{fontSize:12,color:'#374151',marginBottom:2}}>{form.designation || <span style={{color:'#d1d5db'}}>Designation</span>}</div>
                <div style={{fontSize:11,color:'#6b7280',marginBottom:8}}>{form.department ? form.department + ' Department' : <span style={{color:'#d1d5db'}}>Department</span>}</div>

                <div style={{fontSize:10,color:'#374151',lineHeight:2.1,marginBottom:8}}>
                  <div><i className="bi bi-geo-alt-fill me-2" style={{color:'#1e8449'}}/>{form.address}</div>
                  <div><i className="bi bi-telephone-fill me-2" style={{color:'#1e8449'}}/>{form.phone}</div>
                  {form.email && <div><i className="bi bi-envelope-fill me-2" style={{color:'#1e8449'}}/>{form.email}</div>}
                  <div><i className="bi bi-globe me-2" style={{color:'#1e8449'}}/>{form.website}</div>
                </div>

                <div style={{height:2,background:'linear-gradient(90deg,#1a5276,#1e8449)',borderRadius:2,marginBottom:8}}/>

                <div style={{minHeight:80,border:'1.5px dashed #e5e7eb',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',color:'#d1d5db',fontSize:12,fontStyle:'italic'}}>
                  [ Official Letter Content Goes Here ]
                </div>

                <div style={{display:'flex',justifyContent:'flex-end',marginTop:8}}>
                  <div style={{textAlign:'center'}}>
                    <div style={{borderTop:'1.5px solid #374151',width:130,marginBottom:4}}/>
                    <div style={{fontSize:10,color:'#6b7280'}}>Authorized Signatory</div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{background:'linear-gradient(135deg,#1e8449,#1a5276)',padding:'6px 17px',color:'rgba(255,255,255,0.9)',fontSize:9,textAlign:'center'}}>
                TSE Workspace Office  &bull;  Green City, Sector-10, Lucknow - 226010  &bull;  {form.website}
              </div>
            </div>

            <div className="mt-3">
              <button className="btn btn-success rounded-3 fw-semibold w-100" onClick={handleDownloadPDF}>
                <i className="bi bi-file-earmark-pdf-fill me-2"/>Download as PDF (A4)
              </button>
            </div>
          </div>
        </div>
      </section></main></div>
    </>
  );
}
export default LetterheadEditor;
